import { useEffect, useState } from "react";

import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { Alert, Button, Card, CardContent, Checkbox, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip, Typography } from "@mui/material";

import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import { AiOutlineFileText } from "react-icons/ai";

import moment from "moment";

import { usePages } from "../../../hooks/usePages";

import { TablePaginationActions } from "../../../helpers/TablePaginationActions";

import { openSnackbar } from '../../../store/slices/SnackbarSlice';

import { TableTheadStyle, TablePaddingStyle } from "../../../styles/TableStyle";

import { Layout } from "../../../components/Layout";
import { BoxHeaderInformacion } from "../../../components/BoxHeaderInformacion";

import { DialogDocumento } from "../../../dialogs/DialogDocumento";

import { ErrorType } from "../../../types/ErrorType";

import { NotificacionDetalleResponse } from "../../../interfaces/tribunal/ListaEstradosInterface";

import { DetalleListaEstrados, FinalizarListaEstrados, GenerarListaEstrados, PublicarListaEstrados } from "../../../connections/tribunal/ListaEstradosConnection";

interface Errors {
    type?: ErrorType;
    finalizar?: string;
}

export const GenerarListaEstradosPage = () => {

    const [rows, setRows] = useState<NotificacionDetalleResponse[]>([]);

    const { id } = useParams<{ id: string }>();

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [idEstatus, setIdEstatus] = useState(0);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [numeroSeguimiento, setNumeroSeguimiento] = useState(0);

    const [errors, setErrors] = useState<Errors>({});

    const [loadingGenerar, setLoadingGenerar] = useState(false);
    const [loadingFinalizar, setLoadingFinalizar] = useState(false);

    const [documento, setDocumento] = useState('');
    const [openDocumento, setOpenDocumento] = useState(false);

    const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePages( 10 );

    const handleGenerar = () => {

        if (!id) return;

        setLoadingGenerar(true);

        const selectedRows = rows.filter(r => selectedIds.includes(r.id));

        PublicarListaEstrados({ idListaEstrados: id, notificaciones: selectedRows }).then(response => {

            setTimeout(() => {

                if (response.success) {
                    setDocumento(response.listaEstradosFirmado || response.listaEstrados);
                    setOpenDocumento(true);
                    setRows(response.notificacionDetalle ?? rows);
                    setIdEstatus(49);
                } else {
                    setErrors({ finalizar: response.message ?? 'Error al generar', type: 'error' });
                }
                setLoadingGenerar(false);
            }, 700);
        });
    };

    const handleFinalizar = () => {

        if (!id) return;

        setLoadingFinalizar(true);
        FinalizarListaEstrados({ idListaEstrados: id }).then(response => {

            setTimeout(() => {
                if (response.success) {
                    dispatch(openSnackbar({ message: 'Se publicó la lista de estrados correctamente', variant: 'success' }));

                    navigate('/tribunal/lista-estrados/consultar/');
                } else {
                    setErrors({ finalizar: response.message ?? 'Error al finalizar', type: 'error' });
                }
                setLoadingFinalizar(false);
            }, 700);
        });
    };

    useEffect(() => {

        if (id) {
            DetalleListaEstrados({ idListaEstrados: id }).then(response => {
                if (response.success) {
                    setRows(response.notificacionDetalle ?? []);
                    setIdEstatus(response.idEstatus);
                    setNumeroSeguimiento(response.numeroSeguimiento);
                    setErrors({});
                    if (response.listaEstradosFirmado) setDocumento(response.listaEstradosFirmado);
                    else if (response.listaEstrados) setDocumento(response.listaEstrados);
                } else {
                    setErrors({ finalizar: response.message ?? 'Ocurrió un error al cargar', type: 'error' });
                }
            });
            return;
        }

        GenerarListaEstrados().then(response => {
            if (response.idListaEstrados) {
                navigate(`/tribunal/lista-estrados/generar/${response.idListaEstrados}`, { replace: true });
            } else {
                setErrors({ finalizar: response.message ?? 'No se encontraron notificaciones para publicar', type: 'warning' });
            }
        });

    }, [id, navigate]);

    const handleSelectAll = () => {

        if ( selectedIds.length === rows.length ) setSelectedIds( [] );
        else setSelectedIds( rows.map(r => r.id) );
    };

    const handleSelectRow = (id: number) => {
        setSelectedIds( prev => prev.includes( id ) ? prev.filter( i => i !== id ) : [...prev, id] );
    };

    return (
        <Layout breadcrumbs={ [{ text: 'Tribunal' }, { text: 'Lista de estrados' }, { text: 'Generar lista de estrados' }] } backgroundPaper={ false }>

            <Grid container spacing={ 3 }>

                <Grid size={{ md: 12, xs: 12 }}>

                    <Card>

                        <CardContent sx={{ p: 3 }}>

                            <Grid container spacing={ 3 }>

                                <Grid size={{ md: 12, xs: 12 }}>
                                    <BoxHeaderInformacion numeroSeguimiento={`GLES-${numeroSeguimiento}`} fecha={ new Date() } />
                                </Grid>

                                <Grid size={{ md: 8, xs: 12 }}></Grid>

                                <Grid size={{ md: 4, xs: 12 }}>

                                    <Grid container spacing={ 3 }>

                                        <Grid size={{ md: 8, xs: 12 }}>
                                            <Button
                                                fullWidth
                                                color="secondary"
                                                variant="outlined"
                                                loading={ loadingGenerar }
                                                onClick={ handleGenerar }
                                                disabled={ loadingGenerar || selectedIds.length === 0 || idEstatus !== 50 }
                                            >
                                                Generar lista de estrados
                                            </Button>
                                        </Grid>

                                        <Grid size={{ md: 4, xs: 12 }}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                loading={ loadingFinalizar }
                                                onClick={ handleFinalizar }
                                                disabled={ loadingFinalizar || idEstatus !== 49 }
                                            >
                                                Finalizar
                                            </Button>
                                        </Grid>

                                    </Grid>

                                </Grid>

                            </Grid>

                        </CardContent>

                    </Card>

                </Grid>

                <Grid size={{ md: 12, xs: 12 }}>

                    <TableContainer sx={{ borderRadius: 3 }} component={ Paper }>

                        <Table>

                            <TableHead sx={{ ...TableTheadStyle }}>

                                <TableRow sx={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>

                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '10%', fontWeight: 'bold' }}>Fecha</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '10%', fontWeight: 'bold' }}>Referencia</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '10%', fontWeight: 'bold' }}>Tipo de juicio</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '12%', fontWeight: 'bold' }}>Tipo de acuerdo</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '17%', fontWeight: 'bold' }}>Actor(es)</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '16%', fontWeight: 'bold' }}>Demandado(s)</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '10%', fontWeight: 'bold' }}>Se notifica a</TableCell>
                                    <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '10%', fontWeight: 'bold', textAlign: 'end' }}>
                                        <Tooltip title='Seleccionar todo'>
                                            <Checkbox
                                                checked={ rows.length > 0 && selectedIds.length === rows.length }
                                                indeterminate={ selectedIds.length > 0 && selectedIds.length < rows.length }
                                                onChange={ handleSelectAll }
                                                sx={{ bgcolor: 'white', borderRadius: 2, ':hover': { bgcolor: 'white' } }}
                                            />
                                        </Tooltip>
                                    </TableCell>

                                </TableRow>

                            </TableHead>

                            <TableBody>

                                {
                                    (rowsPerPage > 0
                                        ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        : rows
                                    ).map(({ id, fechaAcuerdo, referencia, otraReferencia, tipoJuicio, tipoParte, tipoAcuerdo, subTipoAcuerdo, actores, demandados, documentoAcuerdoFirmado }, index) => (

                                        <TableRow key={ index } >

                                            <TableCell>

                                                <Typography variant='subtitle1'>
                                                    {moment(fechaAcuerdo, 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY')}
                                                </Typography>

                                                <Typography variant='caption' color='text.secondary'>
                                                    {moment(fechaAcuerdo, 'DD-MM-YYYY HH:mm').format('hh:mm a')}
                                                </Typography>

                                            </TableCell>

                                            <TableCell>

                                                <Typography variant="subtitle1">
                                                    <strong>{referencia}</strong>
                                                </Typography>

                                                {otraReferencia && <Typography variant="subtitle2" fontWeight={ 'normal' } fontSize={ 14 }>F. {otraReferencia}</Typography>}

                                            </TableCell>

                                            <TableCell> {tipoJuicio} </TableCell>

                                            <TableCell>
                                                {
                                                    subTipoAcuerdo
                                                        ?
                                                        <>
                                                            <Typography variant="subtitle1" fontWeight={ 'bold' }>{tipoAcuerdo}</Typography>
                                                            <Typography variant="subtitle2" fontWeight={ 'normal' } fontSize={ 14 }>({subTipoAcuerdo})</Typography>
                                                        </>
                                                        :
                                                        <Typography variant="subtitle1" fontWeight={ 'bold' }>{tipoAcuerdo}</Typography>
                                                }
                                            </TableCell>

                                            <TableCell>{actores?.join(', ')}</TableCell>

                                            <TableCell>{demandados?.join(', ')}</TableCell>

                                            <TableCell>
                                                <Typography variant="subtitle2">
                                                    SE NOTIFICA A LA PARTE {tipoParte?.toUpperCase()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'end' }}>

                                                <Tooltip title='Visualizar acuerdo'>
                                                    <IconButton
                                                        color="primary"
                                                        disabled={ !documentoAcuerdoFirmado }
                                                        onClick={ () => { setDocumento(documentoAcuerdoFirmado); setOpenDocumento(true); } }>
                                                        <AiOutlineFileText fontSize={ 28 } />
                                                    </IconButton>
                                                </Tooltip>

                                                <Checkbox
                                                    checked={ selectedIds.includes(id) }
                                                    onChange={ () => handleSelectRow(id) }
                                                />

                                            </TableCell>

                                        </TableRow>

                                    ))
                                }

                                {
                                    rows.length === 0
                                    &&
                                    <TableRow>
                                        <TableCell
                                            colSpan={ 8 }
                                            align="center"
                                        >
                                            <Typography variant="caption" color='secondary'> No se encontraron registros </Typography>
                                        </TableCell>
                                    </TableRow>
                                }

                            </TableBody>

                            <TableFooter>

                                <TableRow>

                                    <TableCell colSpan={ 2 }>
                                        <Typography variant='body2' color='black' sx={{ fontWeight: 'bold' }}> Total de registros: {rows.length} </Typography>
                                    </TableCell>

                                    <TablePagination
                                        rowsPerPageOptions={ [5, 10, 15, 25, 50, 75, 100, 150, 200, 500] }
                                        colSpan={ 8 }
                                        count={ rows.length }
                                        rowsPerPage={ rowsPerPage }
                                        page={ page }
                                        onPageChange={ handleChangePage }
                                        onRowsPerPageChange={ handleChangeRowsPerPage }
                                        ActionsComponent={ TablePaginationActions }
                                        labelRowsPerPage={ 'Filas por página:' }
                                        labelDisplayedRows={
                                            ({ from, to, count }) => {
                                                return '' + from + '-' + to + ' de ' + count
                                            }
                                        }
                                    />

                                </TableRow>

                            </TableFooter>

                        </Table>

                    </TableContainer>

                </Grid>

                {
                    errors.finalizar
                    &&
                    <Grid size={{ md: 12, xs: 12 }} mt={ 3 }>
                        <Alert icon={ <InfoOutlineIcon /> } variant='filled' severity={ errors.type }>
                            {errors.finalizar}
                        </Alert>
                    </Grid>
                }

            </Grid>

            <DialogDocumento
                documento={ documento }
                openDialog={ openDocumento }
                handleCloseDialog={ () => setOpenDocumento(false) }
            />

        </Layout>
    )
}
